<template>
    <div class="reservations d-flex flex-column">
        <div>
            <navigator v-on:mainFilterChanged="mainFilterChanged"></navigator>
        </div>

        <div class="col-sm-12 flexGrow1 overflow-auto">
            <component
                :is="selectedAreaName"
                :reservationMainFilters="navigatorFilters || {}"
                :pagination="pagination"
                :selectedTab="currentSelectedTab"
                @paginationChanged="paginationChanged"
                @resetPagination="resetPagination">
            </component>
        </div>
    </div>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex'
    import { eventHub } from '@/main'
    import { RESERVATION_LIST_TYPE } from '@/enums/reservationListType'
    
    import Navigator from './Navigator'
    import ReservationsByDepartureDate from './ReservationsByDepartureDate'
    import ReservationsByPickupDate from './ReservationsByPickupDate'
    import ReservationsBySaleDate from './ReservationsBySaleDate'

    export default {
        data() {
            return {
                navigatorFilters: null,
                currentSelectedTab: 'reservations',
                pagination: {
                    currentPage: 1,
                },
                availableAreas: [
                    {
                        componentName: 'ReservationsByDepartureDate',
                        viewId: RESERVATION_LIST_TYPE.DEPARTURE_DATE
                    },
                    {
                        componentName: 'ReservationsByPickupDate',
                        viewId: RESERVATION_LIST_TYPE.PICKUP_DATE
                    },
                    {
                        componentName: 'ReservationsBySaleDate',
                        viewId: RESERVATION_LIST_TYPE.SALES_DATE
                    },
                ]
            }
        },
        created() {
            this.handleRouteChange(this.$route);
            eventHub.$on('reservations:selectedTab', (tab) => { this.currentSelectedTab = tab; });
            eventHub.$on('paymentLinks:requestDateRange', () => {
                if (this.navigatorFilters) {
                    eventHub.$emit('paymentLinks:dateRangeChanged', {
                        dateRange: this.navigatorFilters.dateRange,
                        filterDateType: this.navigatorFilters.selectedView.id
                    });
                }
            });
        },
        beforeDestroy() {
            eventHub.$off('reservations:selectedTab');
            eventHub.$off('paymentLinks:requestDateRange');
        },
        watch: {
            $route: {
                handler(newRoute) {
                    this.handleRouteChange(newRoute);
                },
            },
        },
        computed: {
            ...mapGetters([
                'account'
            ]),
            selectedAreaName() {
                if (!this.navigatorFilters) {
                    return this.availableAreas[0].componentName;
                } else {
                    return this.availableAreas.filter(area => {
                        return area.viewId == this.navigatorFilters.selectedView.id;
                    })[0].componentName;
                }
            }
        },
        methods: {
            ...mapActions([
                'setSelectedReservation',
                'openModal',
                'pushReservationToStore',
                'setSelectedEventListReservations',
                'setEventReservations'
            ]),
            handleRouteChange(newRoute) {
                if (!newRoute) return;

                if (newRoute.name === 'reservations') {
                    this.setSelectedEventListReservations(null);
                    this.$forceUpdate();
                }

                const urlEventId = newRoute.params.eventId;
                if (urlEventId) {
                    this.loadEventReservations(urlEventId);
                }

                const urlReservationId = newRoute.params.reservationId;
                if (urlReservationId) {
                    this.openSpecificReservation(urlReservationId);
                }
            },
            loadEventReservations(eventId) {
                if (!eventId) return;
                this.setSelectedEventListReservations({ id: eventId });

                return this.setEventReservations(eventId).catch(() => {});
            },
            mainFilterChanged(selectedFilters) {
                // When the filters on the navigation bar are changed (viewOptions and data range)
                this.pagination.currentPage = 1;
                this.navigatorFilters = selectedFilters;
                eventHub.$emit('paymentLinks:dateRangeChanged', {
                    dateRange: selectedFilters.dateRange,
                    filterDateType: selectedFilters.selectedView.id
                });
            },
            resetPagination() {
                this.pagination.currentPage = 1;
            },
            triggerModal(reservation, modal) {
                this.setSelectedReservation({payload: reservation, isToResetFlags: true});
                this.openModal(modal);
            },
            openSpecificReservation(reservationId) {
                let vm = this;

                axios.get('api/reservation/FindReservationBySupplier', {
                    params: {
                        supplierId: vm.account.supplierId,
                        reservationId: reservationId
                    }
                }).then((response) => {
                    if (response !== null && response !== undefined && response.status == 200) {
                        if (response.data !== null) {
                            // Ticket exists
                            this.pushReservationToStore(response.data);
                            this.triggerModal(response.data, 'ReservationEditV2');
                        } else {
                            vm.$toasted.info('Ticket #'+reservationId+' not found');
                            vm.ticketNr = null;
                        }
                    }
                }).catch((error) => {});
            },
            paginationChanged(newPagination) {
                this.pagination = newPagination;
            }
        },
        components: {
            Navigator,
            ReservationsByDepartureDate,
            ReservationsByPickupDate,
            ReservationsBySaleDate
        }
    }
</script>

<style lang="scss">
    @import '../../assets/scss/_variables.scss';
    @import '../../assets/scss/_utility.scss';

    .reservations-filters-height {
        height: 2.399rem;
    }

    .reservations-filters-circle {
        position: absolute;
        height: 8px;
        width: 8px;
        border-radius: 10px;
        top: 5px;
        right: 5px;
    }

    .reservations-min-height {
        min-height: 81vh;
    }

    .results-per-page { 
        color: #35495e;
    }

    // Loader | This is used on other places like on PeopleAgents.vue (agents list)
    .loaderHolder {
        background: #C5D0DE;
        padding: 7px;
        width: 52px;
        
        @extend .radius-normal;
    }

    .loader,
    .loader:before,
    .loader:after {
        border-radius: 50%;
        width: 1em;
        height: 1em;
        -webkit-animation-fill-mode: both;
        animation-fill-mode: both;
        -webkit-animation: load7 1.8s infinite ease-in-out;
        animation: load7 1.8s infinite ease-in-out;
    }

    .loader {
        color: #8CA0B3;
        font-size: 10px;
        position: relative;
        text-indent: -9999em;
        -webkit-transform: translateZ(0);
        -ms-transform: translateZ(0);
        transform: translateZ(0);
        -webkit-animation-delay: -0.16s;
        animation-delay: -0.16s;
    }

    .loader:before,
    .loader:after {
        content: '';
        position: absolute;
        top: 0;
    }

    .loader:before {
        left: -1.3em;
        -webkit-animation-delay: -0.32s;
        animation-delay: -0.32s;
    }

    .loader:after {
        left: 1.3em;
    }

    @-webkit-keyframes load7 {
        0%,
        80%,
        100% {
            box-shadow: 15px 0 0 -1.3em;
        }
        40% {
            box-shadow: 15px 0 0 0;
        }
    }

    @keyframes load7 {
        0%,
        80%,
        100% {
            box-shadow: 15px 0 0 -1.3em;
        }
        40% {
            box-shadow: 15px 0 0 0;
        }
    }
</style>



// WEBPACK FOOTER //
// Reservations.vue?271710e0